import React, { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { LoadingSpinner } from "ctech-react-components";

import logisticsApi from "services/logisticsApi";
import ordersApi from "services/ordersApi";
import dealerCareApi from "services/dealerCareApi";
import catalogApi from "services/CatalogApi";
import cartApi from "services/cartApi";
import checkoutApi from "services/checkoutApi";
import billingApi from "services/billingApi";
import paymentApi from "services/paymentApi";
import authApi from "services/authApi";
import {
  setAccessTokenGetter,
  setupApiAuthInterceptor,
} from "services/apiAuth";
import { clarityManager } from "libs/clarity";
import { useAppSelector } from "store/hooks";
import {
  clearLegacyAuthStorage,
  hasOidcUrlParams,
  getOidcUrlError,
  isAccessDeniedError,
  cleanOidcUrlAndRedirect,
} from "utils/oidcUrl";

const apiInstances = [
  logisticsApi,
  ordersApi,
  dealerCareApi,
  catalogApi,
  cartApi,
  checkoutApi,
  billingApi,
  paymentApi,
  authApi,
];

apiInstances.forEach(setupApiAuthInterceptor);

function AppSpinner() {
  const { isSpinnerVisible } = useAppSelector((state) => state.appConfig);
  return <LoadingSpinner isSpinnerVisible={isSpinnerVisible} />;
}

const AuthTokenSync = ({ children }: { children: React.ReactNode }) => {
  const auth = useAuth();

  useEffect(() => {
    if (!auth.isAuthenticated) {
      clearLegacyAuthStorage();
    }
  }, [auth.isAuthenticated]);

  useEffect(() => {
    setAccessTokenGetter(() => auth.user?.access_token);

    if (auth.isAuthenticated && auth.user?.profile.sub) {
      clarityManager.identify(auth.user.profile.sub);
      clearLegacyAuthStorage();
    }
  }, [auth.user?.access_token, auth.isAuthenticated, auth.user?.profile.sub]);

  useEffect(() => {
    if (auth.isLoading || auth.activeNavigator) {
      return;
    }

    const oidcUrlError = getOidcUrlError();

    if (oidcUrlError && isAccessDeniedError(oidcUrlError.error)) {
      cleanOidcUrlAndRedirect("/unauthorized");
      return;
    }

    if (auth.error) {
      const authError = auth.error as { error?: string; message?: string };

      if (
        isAccessDeniedError(authError.error) ||
        isAccessDeniedError(authError.message)
      ) {
        cleanOidcUrlAndRedirect("/unauthorized");
        return;
      }
    }

    if (hasOidcUrlParams() || auth.error) {
      cleanOidcUrlAndRedirect("/");
    }
  }, [auth.isLoading, auth.activeNavigator, auth.error]);

  const showGlobalSpinner =
    (auth.isLoading || auth.activeNavigator) && auth.isAuthenticated;

  if (showGlobalSpinner) {
    return <AppSpinner />;
  }

  return children as JSX.Element;
};

export default AuthTokenSync;
